import type { WrappedComponentType } from './types';
import type { HubGeneral } from '../../../constants';
import { P, Span, Heading, Body, Utility, Label, H1, H2, H3, H4, H5, H6, SubHeading } from '@workramp/ui';
import { useHubTheming } from '../../../providers/useHubTheming';
import { getMutedColor } from '../../utils';
import { StyledFontWrapper } from './ThemeText.styles';

type ThemeTextType = {
  [key: string]: WrappedComponentType;
};

const HUB_GENERAL = 'hub.general';
const withColor =
  (WrappedComponent: WrappedComponentType, displayName: string) =>
  ({ themeTextColor, themeFontFamily, themeFontSize, isMuted = false, children, ...props }) => {
    const { renderedThemeSettings } = useHubTheming();
    const hubGeneralSettings: HubGeneral = renderedThemeSettings.find((ts) => ts.category === HUB_GENERAL).settings;
    const { colors: defaultHubColors, typography: defaultHubTypography } = hubGeneralSettings;

    const isHeading = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Heading', 'SubHeading'].includes(displayName);

    const hubDefaultFontFamily = isHeading ? defaultHubTypography.heading : defaultHubTypography.body;
    const hubDefaultFontSize = isHeading ? defaultHubTypography.headingSize : defaultHubTypography.bodySize;
    const hubDefaultTextColor = defaultHubColors.text;

    const textColor = isMuted
      ? getMutedColor(themeTextColor || hubDefaultTextColor)
      : themeTextColor || hubDefaultTextColor;

    return withBaseColor(WrappedComponent)({
      ...props,
      themeTextColor: textColor,
      themeFontFamily: themeFontFamily || hubDefaultFontFamily,
      themeFontSize: themeFontSize || hubDefaultFontSize,
      children,
    });
  };

const withBaseColor =
  (WrappedComponent: WrappedComponentType) =>
  ({ themeTextColor, themeFontFamily, themeFontSize, children, ...props }) => (
    <WrappedComponent {...props} color={themeTextColor}>
      <StyledFontWrapper $fontSize={themeFontSize} $fontFamily={themeFontFamily}>
        {children}
      </StyledFontWrapper>
    </WrappedComponent>
  );

export const ThemeText: ThemeTextType = {
  Span: withColor(Span, 'Span'),
  P: withColor(P, 'P'),
  H1: withColor(H1, 'H1'),
  H2: withColor(H2, 'H2'),
  H3: withColor(H3, 'H3'),
  H4: withColor(H4, 'H4'),
  H5: withColor(H5, 'H5'),
  H6: withColor(H6, 'H6'),
  Label: withColor(Label, 'Label'),
  Heading: withColor(Heading, 'Heading'),
  SubHeading: withColor(SubHeading, 'SubHeading'),
  Body: withColor(Body, 'Body'),
  Utility: withColor(Utility, 'Utility'),
};

export const BaseThemeText: ThemeTextType = {
  Span: withBaseColor(Span),
  P: withBaseColor(P),
  H1: withBaseColor(H1),
  H2: withBaseColor(H2),
  H3: withBaseColor(H3),
  H4: withBaseColor(H4),
  H5: withBaseColor(H5),
  H6: withBaseColor(H6),
  Label: withBaseColor(Label),
  Heading: withBaseColor(Heading),
  SubHeading: withBaseColor(SubHeading),
  Body: withBaseColor(Body),
  Utility: withBaseColor(Utility),
};
